import { useEffect } from "react";
import { useNavigate } from "react-router";
import { ActionButton, Callout, IconBrick, IconChevronRightLine, IconGearLine, IconPersonFill, IconPictureLine, IconSquare2StackedLine, IconTicketLine, ListItem, TopNavigation } from "@hamlak/ui";
import { isApiError } from "@/api/client";
import { t } from "@/i18n/ko";
import { platform } from "@/platform";
import type { StoreState } from "@/features/store/StorePage";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { BottomTabs } from "@/components/BottomTabs";
import { useMe } from "./useMe";
import "./me.css";

/**
 * F07-01 마이페이지(1.16, Figma 43:869). GET /me → 계정(사진·닉네임 → /me/edit · 회원번호) · 대화 프로필 행(이름 · 기본 채팅 프로필·성별 → /profile/:id/edit, spec 7절) · 보유 브릭 카드(충전 → /store) ·
 * 결제(결제 내역·브릭 사용 내역) / 기타(설정) 섹션. 헤더 톱니 = 설정.
 * 실패하면 authStore 캐시로 표시 + Snackbar(캐시 없으면 재시도).
 */
export function MePage() {
  const navigate = useNavigate();
  const me = useMe();
  const balance = useAuthStore((s) => s.balance);
  const profile = useAuthStore((s) => s.profile);
  const account = useAuthStore((s) => s.account);
  const publicId = useAuthStore((s) => s.user?.publicId ?? null);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const copyPublicId = async (id: string) => {
    try {
      await platform.device.copy(id);
      showSnackbar(t("me.id.copied"));
    } catch {
      showSnackbar(t("chat.copyFailed"));
    }
  };

  const { isError, error, refetch } = me;
  useEffect(() => {
    if (!isError) return;
    showSnackbar(isApiError(error) && error.code === "NETWORK" ? t("error.network") : t("me.loadError"), { label: t("common.retry"), onClick: () => void refetch() });
    // error 객체가 바뀔 때마다 1회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error]);

  const hasCache = balance !== null || profile !== null || account !== null;

  return (
    <main className="page" data-testid="me-page">
      <TopNavigation
        type="pageTitle"
        title={t("page.me")}
        left={null}
        right={
          <button type="button" className="me__navButton" aria-label={t("page.settings")} onClick={() => navigate("/settings")}>
            <IconGearLine />
          </button>
        }
      />
      {me.isPending && !hasCache ? (
        <div className="page-placeholder" role="status">
          {t("common.loading")}
        </div>
      ) : me.isError && !hasCache ? (
        <div className="page-body">
          <Callout tone="critical" title={t("me.loadError")} />
          <div style={{ marginTop: 12 }}>
            <ActionButton variant="neutralWeak" size="small" onClick={() => void me.refetch()}>
              {t("common.retry")}
            </ActionButton>
          </div>
        </div>
      ) : (
        <>
          <section className="me__profile">
            <button type="button" className="me__account" aria-label={t("me.account.edit")} onClick={() => navigate("/me/edit")}>
              {account?.photoUrl ? <img className="me__avatar" src={account.photoUrl} alt={t("me.account.photoAlt")} /> : (
                // 사진 없으면 사람 아이콘 placeholder(AC0-232)
                <span className="me__avatar me__avatar--empty" data-testid="avatar-placeholder" aria-hidden>
                  <IconPersonFill size={32} />
                </span>
              )}
              <span className="me__name">{account?.displayName ?? t("me.account.noName")}</span>
              <IconChevronRightLine size={16} className="me__chevron" aria-hidden />
            </button>
            {publicId ? (
              <button type="button" className="me__publicId" aria-label={t("me.id.copy", { id: publicId })} onClick={() => void copyPublicId(publicId)}>
                <span>ID : {publicId}</span>
                <IconSquare2StackedLine size={14} aria-hidden />
              </button>
            ) : null}
          </section>
          {profile ? (
            <section className="me__chatProfile">
              <p className="me__listHeaderPlain">{t("me.chatProfile.title")}</p>
              <button type="button" className="me__chatProfileRow" aria-label={t("me.chatProfile.edit")} onClick={() => navigate(`/profile/${profile.id}/edit`)}>
                <span className="me__chatProfileText">
                  <span className="me__chatProfileName">{profile.name}</span>
                  <span className="me__profileKind">
                    {profile.gender === "none" ? t("me.profile.default") : t("me.profile.defaultWithGender", { gender: t(`profile.gender.${profile.gender}`) })}
                  </span>
                </span>
                <IconChevronRightLine size={16} className="me__chevron" aria-hidden />
              </button>
            </section>
          ) : null}
          <section className="me__bricks" aria-label={t("me.bricks.title")}>
            <IconBrick size={28} aria-hidden />
            <div className="me__bricksText">
              <p className="me__bricksLabel">{t("me.bricks.title")}</p>
              <p className="me__bricksCount">{(balance ?? 0).toLocaleString("ko-KR")}</p>
            </div>
            <ActionButton variant="brandSolid" size="small" onClick={() => navigate("/store", { state: { storeSource: "mypage" } satisfies StoreState })}>
              {t("me.bricks.charge")}
            </ActionButton>
          </section>
          <nav className="me__menu">
            <p className="me__listHeader">{t("me.section.payment")}</p>
            <ListItem prefixIcon={<IconSquare2StackedLine />} title={t("me.menu.purchase")} onClick={() => navigate("/me/bricks?tab=purchase")} />
            <ListItem prefixIcon={<IconPictureLine />} title={t("me.menu.usage")} onClick={() => navigate("/me/bricks?tab=usage")} />
            <ListItem prefixIcon={<IconTicketLine />} title={t("me.menu.coupon")} onClick={() => navigate("/me/coupon")} />
            <p className="me__listHeader">{t("me.section.etc")}</p>
            <ListItem prefixIcon={<IconGearLine />} title={t("me.menu.settings")} onClick={() => navigate("/settings")} />
          </nav>
        </>
      )}
      <BottomTabs current="me" />
    </main>
  );
}
